import { useState } from 'react';
import type { FormEvent } from 'react';
import { Alert, Box, Button, Link as MuiLink, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthShell } from './AuthShell';
import { useAuth } from '../../contexts/AuthContext';

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Set by the setup flow (GettingStarted -> /login?welcome=1) after a password
  // is created, so we confirm success here instead of redirecting silently.
  const justCompletedSetup = searchParams.get('welcome') === '1';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Dev/staging only: warn that this is not the production site. Gated on
  // VITE_APP_ENV (set to 'prod' on the main build) so prod never shows it.
  const isProd = import.meta.env.VITE_APP_ENV === 'prod';

  // Always send the user to "/" after login so RoleHome routes them to
  // their role-specific home. We deliberately don't honour a "from"
  // location any more — logging in after an explicit logout was
  // surfacing wherever the user was when they signed out (e.g.
  // /settings), which felt wrong. Always-home is simpler and matches
  // expectation.
  const returnTo = '/';

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // No audience toggle — try admin first, fall back to entrant if
      // admin either errors or succeeds with empty roles (which happens
      // when an entrant hits the admin OIDC project: Zitadel's
      // projectRoleCheck is off, so a token issues, but the project's
      // role claim is empty). Admin and entrant accounts live on
      // separate Zitadel projects with no overlap in practice, so a
      // real admin always succeeds first try and an entrant always
      // succeeds on the retry. Wrong credentials fail both attempts.
      let u: Awaited<ReturnType<typeof login>>;
      try {
        u = await login(email.trim(), password, 'admin');
      } catch {
        u = await login(email.trim(), password, 'entrant');
      }
      if (!u || u.roles.length === 0) {
        u = await login(email.trim(), password, 'entrant');
      }
      navigate(returnTo, { replace: true });
    } catch (err: unknown) {
      setError(friendlyLoginError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell>
      <Stack spacing={3}>
        <Stack spacing={0.5}>
          <Typography
            variant="h4"
            component="h1"
            fontWeight={700}
          >
            Sign in
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back. Sign in with your WAF credentials.
          </Typography>
        </Stack>

        {!isProd && (
          <Typography variant="body2" color="error" fontWeight={600}>
            This URL has been deprecated and is a development environment only,
            please use the main site:{' '}
            <MuiLink
              href="https://admin.worldbuildingsdirectory.com/"
              color="error"
              underline="always"
            >
              https://admin.worldbuildingsdirectory.com/
            </MuiLink>
          </Typography>
        )}

        {justCompletedSetup && !error && (
          <Alert severity="success">
            Your password has been set. Please sign in with your email and new password.
          </Alert>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              required
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
            <TextField
              label="Password"
              type="password"
              autoComplete="current-password"
              required
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting || !email || !password}
              sx={{ fontWeight: 600 }}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>

            <Stack direction="row" justifyContent="center">
              <MuiLink
                component={RouterLink}
                to="/password-reset"
                variant="body2"
                color="text.secondary"
                underline="hover"
              >
                Forgot password?
              </MuiLink>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </AuthShell>
  );
}

// Map backend auth failures to friendly, user-facing copy. The raw codes
// (invalid_credentials, auth_unavailable, internal, …) and full exceptions
// stay in backend logs/Sentry — the user should never see "internal".
function friendlyLoginError(err: unknown): string {
  const e = err as { response?: { status?: number; data?: { error?: string; message?: string } } };
  // No response at all = network/CORS/server-down.
  if (typeof e !== 'object' || e === null || !e.response) {
    return 'Unable to reach the server — check your connection and try again.';
  }
  const status = e.response.status;
  const code = e.response.data?.error;

  if (status === 429) return 'Too many sign-in attempts. Please wait a minute and try again.';
  if (status === 401 || code === 'invalid_credentials') return 'Incorrect email or password.';
  if (code === 'invalid_request') return 'Please enter your email and password.';
  if (code === 'auth_unavailable' || status === 502 || status === 503 || status === 504) {
    return 'Sign-in is temporarily unavailable. Please try again in a moment.';
  }
  if ((status ?? 0) >= 500 || code === 'internal') {
    return 'Something went wrong on our end. Please try again.';
  }
  return 'Unable to sign in. Please try again.';
}
